import React, { ChangeEvent, useState, forwardRef, useEffect } from 'react';
import { IconCalendarOutline } from '@iwoca/icons';
import { Button, Input } from '@iwoca/orion';
import { isAfter } from 'date-fns';

import styles from './DateInput.module.css';

export const DateInput = forwardRef<
  HTMLButtonElement,
  {
    toggleCalendar: (isOpen: boolean) => void;
    handleChange: (newDate: Date) => void;
    selectedDate: Date | null;
    setSelectedDate: (value: React.SetStateAction<Date | null>) => void;
    defaultDate?: string;
  }
>(
  (
    {
      toggleCalendar,
      handleChange,
      selectedDate,
      setSelectedDate,
      defaultDate,
    },
    ref,
  ) => {
    const [dateInputValue, setDateInputValue] = useState(defaultDate);

    const selectedDateAsString = selectedDate?.toISOString().split('T', 1)[0];

    const handleDateChange = (e: ChangeEvent<HTMLInputElement>) => {
      const dateAsString = e.target.value;
      const updatedDate = new Date(dateAsString);

      if (isAfter(updatedDate, new Date())) {
        handleChange(updatedDate);
      }
      setDateInputValue(dateAsString);
      if (!selectedDate) {
        return;
      }
      setSelectedDate(null);
    };

    useEffect(() => {
      setDateInputValue(defaultDate);
    }, [defaultDate]);

    return (
      <div className={styles.dateInputContainer}>
        <Input
          className={styles.dateInput}
          wrapperClassName={styles.wrapper}
          type="date"
          data-testid="data-picker-input"
          value={selectedDateAsString ? selectedDateAsString : dateInputValue}
          onChange={handleDateChange}
        />
        <div className={styles.buttonContainer}>
          <Button
            className={styles.dateInputButton}
            variant="tertiary"
            onClick={() => toggleCalendar(true)}
            data-testid="data-picker-input-button"
            ref={ref}
          >
            <IconCalendarOutline />
          </Button>
        </div>
      </div>
    );
  },
);

DateInput.displayName = 'DateInput';
